// Domain publication validation V1; 2026-10-31 23:12:19 CST; parent 81bdb256d5b7a94bb5a1265d806ce53aef0e0494.
/**
 * HotX four-entry domain form invariants.
 * Version: V1.0; updated: 2026-09-10 CST; parent baseline: 3a2546b41ee9c4b7731b41f6613a8036eb231bca.
 * Keep protocol defaults, origin normalization, region choices and required-field
 * ordering in one source so domain/free-CDN/pure-IP modes cannot drift apart.
 */
import type { FrontendProtocol, ServiceType } from '../unified-edge/model';

export type AccelerationRegion =
  | 'APAC'
  | 'GLOBAL'
  | 'NORTH_AMERICA'
  | 'EUROPE'
  | 'SOUTH_AMERICA'
  | 'MIDDLE_EAST'
  | 'AFRICA';

export const ACCELERATION_REGION_OPTIONS: Array<{ label: string; value: AccelerationRegion }> = [
  { label: '亚太加速', value: 'APAC' },
  { label: '全球加速', value: 'GLOBAL' },
  { label: '北美加速', value: 'NORTH_AMERICA' },
  { label: '欧洲加速', value: 'EUROPE' },
  { label: '南美加速', value: 'SOUTH_AMERICA' },
  { label: '中东加速', value: 'MIDDLE_EAST' },
  { label: '非洲加速', value: 'AFRICA' }
];

export interface ProtocolSelection {
  httpEnabled: boolean;
  httpsEnabled: boolean;
  tcpEnabled: boolean;
  udpEnabled: boolean;
}

export function createDefaultProtocolSelection() {
  return {
    httpEnabled: true,
    httpsEnabled: true,
    tcpEnabled: false,
    udpEnabled: false,
    originPort80Enabled: false,
    originPort443Enabled: true
  };
}

const protocolField: Record<FrontendProtocol, keyof ProtocolSelection> = {
  HTTP: 'httpEnabled',
  HTTPS: 'httpsEnabled',
  TCP: 'tcpEnabled',
  UDP: 'udpEnabled'
};

export function toggleProtocolSelection(
  current: ProtocolSelection,
  protocol: FrontendProtocol,
  enabled: boolean
) {
  const key = protocolField[protocol];
  const activeCount = Object.values(current).filter(Boolean).length;
  if (!enabled && current[key] && activeCount === 1) {
    return { selection: current, blocked: true };
  }
  return { selection: { ...current, [key]: enabled }, blocked: false };
}

export interface ParsedOriginInput {
  address: string;
  preferredProtocol: 'HTTP' | 'HTTPS';
  explicitPort?: number;
}

export function parseOriginInput(rawValue: string): ParsedOriginInput {
  const value = rawValue.trim();
  if (!value) return { address: '', preferredProtocol: 'HTTPS' };
  const hasScheme = /^https?:\/\//i.test(value);
  try {
    const parsed = new URL(hasScheme ? value : `https://${value}`);
    const explicitPort = parsed.port ? Number(parsed.port) : undefined;
    const preferredProtocol = hasScheme
      ? parsed.protocol.toLowerCase() === 'http:' ? 'HTTP' : 'HTTPS'
      : explicitPort && explicitPort !== 443 ? 'HTTP' : 'HTTPS';
    return {
      address: parsed.hostname.replace(/^\[|\]$/g, ''),
      preferredProtocol,
      ...(explicitPort ? { explicitPort } : {})
    };
  } catch {
    return { address: value.replace(/^https?:\/\//i, '').split('/')[0], preferredProtocol: 'HTTPS' };
  }
}

export interface DomainEntryIssue {
  field: 'name' | 'protocols' | 'originAddress' | 'tcpListenPort' | 'tcpOriginPort'
    | 'udpListenPort' | 'udpOriginPort' | 'plan';
  message: string;
}

export interface DomainEntryValidationInput {
  serviceType: ServiceType;
  name: string;
  originAddress: string;
  selectedProtocols: FrontendProtocol[];
  selectedPlanKey: string;
  tcpListenPort: number;
  tcpOriginPort: number;
  udpListenPort: number;
  udpOriginPort: number;
  requireOrigin?: boolean;
}

function validPort(value: number) {
  return Number.isInteger(value) && value >= 1 && value <= 65535;
}

export function collectDomainEntryIssues(input: DomainEntryValidationInput): DomainEntryIssue[] {
  const issues: DomainEntryIssue[] = [];
  if (!input.name.trim()) {
    issues.push({
      field: 'name',
      message: input.serviceType === 'PURE_IP' ? '请填写纯 IP 服务名称。' : '请输入需要添加的加速域名。'
    });
  }
  if (!input.selectedProtocols.length) {
    issues.push({ field: 'protocols', message: '请至少保留一个加速协议。' });
  }
  if (input.requireOrigin !== false && !input.originAddress.trim()) {
    issues.push({ field: 'originAddress', message: '请填写真实源站地址。' });
  }
  if (input.selectedProtocols.includes('TCP') && !validPort(input.tcpListenPort)) {
    issues.push({ field: 'tcpListenPort', message: 'TCP 访问端口必须为 1 至 65535。' });
  }
  if (input.selectedProtocols.includes('TCP') && !validPort(input.tcpOriginPort)) {
    issues.push({ field: 'tcpOriginPort', message: 'TCP 回源端口必须为 1 至 65535。' });
  }
  if (input.selectedProtocols.includes('UDP') && !validPort(input.udpListenPort)) {
    issues.push({ field: 'udpListenPort', message: 'UDP 访问端口必须为 1 至 65535。' });
  }
  if (input.selectedProtocols.includes('UDP') && !validPort(input.udpOriginPort)) {
    issues.push({ field: 'udpOriginPort', message: 'UDP 回源端口必须为 1 至 65535。' });
  }
  if (!input.selectedPlanKey) {
    issues.push({ field: 'plan', message: '请选择一个可用套餐。' });
  }
  return issues;
}
